import React, { useState, useMemo } from "react";
import {
  LayoutGrid, FileText, TrendingUp, Receipt, PhoneCall, Gauge, Scale, Banknote,
  Send, Sparkles, X, ShieldCheck, AlertTriangle, RefreshCw, Inbox, Search,
  CheckCircle2, Clock, FileSignature, Phone, Mail, CalendarDays, StickyNote, CheckSquare,
} from "lucide-react";

/* Radha — Module 9: CRM Pipeline & Agreements + Conversational Queries (preview)
   Seeded to match 009_crm.sql. Conversational bar mirrors the backend NL parser. */

const C = {
  primary: "#1B54F8", success: "#00A87A", warning: "#C8861A", danger: "#D63B2F",
  ai: "#6E44D4", aiBg: "#F5F3FF", bg: "#F3F2EE", surface: "#FFFFFF",
  border: "#E4E3DE", text: "#08090C", muted: "#7A7975",
};
const mono = "'DM Mono', ui-monospace, Menlo, monospace";
const sans = "'Inter', system-ui, sans-serif";
function fmtINR(n) {
  if (n == null || isNaN(n)) return "—";
  const a = Math.abs(n);
  if (a >= 1e7) return `₹${(a / 1e7).toFixed(2)}Cr`;
  if (a >= 1e5) return `₹${(a / 1e5).toFixed(1)}L`;
  return `₹${a.toLocaleString("en-IN")}`;
}

const STAGES = ["lead", "qualified", "proposal", "negotiation", "won"];
const STAGE_COLOR = { lead: C.muted, qualified: C.primary, proposal: C.warning, negotiation: C.ai, won: C.success, lost: C.danger };

/* 8 deals from 009_crm.sql */
const DEALS = [
  { name: "RIL platform expansion", cust: "Reliance Industries Ltd", stage: "negotiation", amount: 25000000, p: 0.80, owner: "Arjun Sharma", idle: 3, next: "Finalise commercials with procurement" },
  { name: "Infosys renewal + upsell", cust: "Infosys Ltd", stage: "negotiation", amount: 27000000, p: 0.65, owner: "Priya Nair", idle: 5, next: "Send revised SOW" },
  { name: "HDFC platform rollout", cust: "HDFC Bank Ltd", stage: "proposal", amount: 30000000, p: 0.55, owner: "Rohit Kulkarni", idle: 8, next: "Schedule security review" },
  { name: "TCS managed services", cust: "Tata Consultancy Services", stage: "proposal", amount: 18000000, p: 0.60, owner: "Ananya Iyer", idle: 6, next: "Pricing workshop" },
  { name: "Bajaj new logo", cust: "Bajaj Finance Ltd", stage: "won", amount: 16000000, p: 1.00, owner: "Arjun Sharma", idle: 20, next: "Kickoff scheduled" },
  { name: "Wipro expansion", cust: "Wipro Ltd", stage: "qualified", amount: 9000000, p: 0.35, owner: "Priya Nair", idle: 12, next: "Discovery call" },
  { name: "M&M analytics", cust: "Mahindra & Mahindra", stage: "lead", amount: 7000000, p: 0.20, owner: "Rohit Kulkarni", idle: 95, next: "Re-engage sponsor" },
].map(d => ({ ...d, weighted: d.amount * d.p }));

const AGREEMENTS = [
  { title: "Bajaj MSA", party: "Bajaj Finance Ltd", value: 16000000, status: "active" },
  { title: "RIL expansion order form", party: "Reliance Industries Ltd", value: 25000000, status: "sent" },
  { title: "Infosys renewal", party: "Infosys Ltd", value: 27000000, status: "draft" },
];

const ACTIVITIES = {
  "M&M analytics": [{ kind: "note", who: "Rohit Kulkarni", when: "95d ago", text: "Sponsor went quiet after reorg; needs re-engagement." }],
  "RIL platform expansion": [{ kind: "meeting", who: "Arjun Sharma", when: "3d ago", text: "Commercial alignment with procurement; pricing largely agreed." }],
};
const ACT_ICON = { call: Phone, email: Mail, meeting: CalendarDays, note: StickyNote, task: CheckSquare };

const EXAMPLES = ["show idle deals over 90 days", "deals in negotiation above 2 crore", "proposal deals"];

const MODULES = [
  { code: "M1", name: "Collections Copilot", icon: PhoneCall },
  { code: "M2", name: "Dispute Resolution", icon: Scale },
  { code: "M3", name: "Contract MIS", icon: FileText },
  { code: "M4", name: "Revenue Forecast", icon: TrendingUp },
  { code: "M5", name: "Invoicing Engine", icon: Receipt },
  { code: "M6", name: "Payment Reminders", icon: Send },
  { code: "M7", name: "Cash Application", icon: Banknote },
  { code: "M8", name: "Cash Flow Forecast", icon: Gauge },
  { code: "M9", name: "CRM Pipeline", icon: LayoutGrid, active: true },
];

/* mirror of backend crm_logic.parse_nl */
function parseNL(text) {
  const t = (text || "").toLowerCase();
  const f = {};
  for (const st of ["lead", "qualified", "proposal", "negotiation", "won", "lost"]) {
    if (t.includes(st)) { f.stage = st; break; }
  }
  if (t.includes("idle") || t.includes("stalled") || t.includes("stale")) {
    f.open = true;
    const m = t.match(/(\d+)\s*day/);
    f.idle_days_gte = m ? +m[1] : 90;
  }
  if (t.includes("open") && f.open === undefined) f.open = true;
  const m = t.match(/(?:above|over|more than|greater than)\s*₹?\s*([\d.]+)\s*(crore|cr|lakh|l)\b/);
  if (m) f.amount_gte = +m[1] * (m[2].startsWith("c") ? 1e7 : 1e5);
  if (t.includes("weighted")) f.aggregate = "weighted_value";
  return f;
}
function matchesFilter(d, f) {
  if (f.stage && d.stage !== f.stage) return false;
  if (f.open && (d.stage === "won" || d.stage === "lost")) return false;
  if (f.amount_gte != null && d.amount < f.amount_gte) return false;
  if (f.idle_days_gte != null && d.idle < f.idle_days_gte) return false;
  return true;
}

function Badge({ children, color }) {
  return <span style={{ fontFamily: mono, fontSize: 9, letterSpacing: 0.5, textTransform: "uppercase", color, background: `${color}14`, border: `0.5px solid ${color}33`, borderRadius: 20, padding: "3px 8px", whiteSpace: "nowrap" }}>{children}</span>;
}
function KPICard({ label, value, sub, accent }) {
  return <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: "16px 18px", flex: 1, minWidth: 140 }}>
    <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>{label}</div>
    <div style={{ fontFamily: mono, fontSize: 24, fontWeight: 500, color: accent || C.text, marginTop: 8 }}>{value}</div>
    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{sub}</div>
  </div>;
}

export default function Crm() {
  const [view, setView] = useState("data");
  const [drawer, setDrawer] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState(null);

  const open = useMemo(() => DEALS.filter(d => d.stage !== "won" && d.stage !== "lost"), []);
  const kpis = useMemo(() => ({
    open: open.length,
    weighted: open.reduce((s, d) => s + d.weighted, 0),
    idle: open.filter(d => d.idle >= 90).length,
    won: DEALS.filter(d => d.stage === "won").length,
  }), [open]);

  const runQuery = (text) => { setQ(text); setFilter(text.trim() ? parseNL(text) : null); };
  const isHighlighted = (d) => filter && matchesFilter(d, filter);
  const matchCount = filter ? DEALS.filter(d => matchesFilter(d, filter)).length : 0;

  return (
    <div style={{ display: "flex", minHeight: 640, background: C.bg, fontFamily: sans, color: C.text }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Inter:wght@400;500;600&display=swap');
        .nav:hover{background:#ffffff14}.chip:hover{background:${C.bg}}`}</style>

      <aside style={{ width: 224, background: "#0E1118", color: "#E7E9EE", padding: "20px 12px", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 8px 18px" }}>
          <div style={{ width: 26, height: 26, borderRadius: 8, background: C.ai, display: "grid", placeItems: "center", fontFamily: mono, fontWeight: 600 }}>र</div>
          <div style={{ fontWeight: 600 }}>Radha</div>
        </div>
        {MODULES.map(m => { const Icon = m.icon; return (
          <div key={m.code} className="nav" style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 8, marginBottom: 2, cursor: "pointer", background: m.active ? C.primary : "transparent", color: m.active ? "#fff" : "#AEB4C0" }}>
            <Icon size={15} /><span style={{ fontSize: 12.5 }}>{m.name}</span>
            <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 8, opacity: 0.6 }}>{m.code}</span>
          </div>); })}
        <div style={{ marginTop: 18, padding: "0 8px", fontSize: 10, color: "#5A6172" }}>Bharat Industries Ltd</div>
      </aside>

      <main style={{ flex: 1, padding: "22px 26px", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontFamily: mono, fontSize: 10, color: C.muted, letterSpacing: 0.6 }}>M9 · CRM PIPELINE & AGREEMENTS + CONVERSATIONAL QUERIES</div>
            <h1 style={{ fontSize: 22, fontWeight: 600, margin: "4px 0 0" }}>Sales Pipeline</h1>
          </div>
          <div style={{ display: "flex", gap: 4, background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 10, padding: 4 }}>
            {["data", "loading", "empty", "error"].map(s => (
              <button key={s} onClick={() => setView(s)} style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5, border: "none", borderRadius: 7, padding: "5px 9px", cursor: "pointer", background: view === s ? C.text : "transparent", color: view === s ? "#fff" : C.muted }}>{s}</button>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
          <KPICard label="Open Deals" value={kpis.open} sub="in pipeline" accent={C.primary} />
          <KPICard label="Weighted Pipeline" value={fmtINR(kpis.weighted)} sub="amount × win %" accent={C.ai} />
          <KPICard label="Idle Deals" value={kpis.idle} sub="≥90 days silent" accent={C.warning} />
          <KPICard label="Won" value={kpis.won} sub="this period" accent={C.success} />
        </div>

        {/* Conversational query bar */}
        <div style={{ background: C.surface, border: `0.5px solid ${filter ? C.ai : C.border}`, borderRadius: 12, padding: "12px 14px", marginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Sparkles size={16} color={C.ai} />
            <input value={q} onChange={e => runQuery(e.target.value)} placeholder="Ask in plain English — e.g. “deals in negotiation above 2 crore”"
              style={{ border: "none", outline: "none", fontSize: 13.5, flex: 1, background: "transparent", color: C.text }} />
            {filter && <span style={{ fontFamily: mono, fontSize: 10, color: C.ai }}>{matchCount} match{matchCount !== 1 ? "es" : ""}</span>}
            {q && <X size={15} color={C.muted} style={{ cursor: "pointer" }} onClick={() => runQuery("")} />}
          </div>
          {filter ? (
            <div style={{ marginTop: 8, fontFamily: mono, fontSize: 10.5, color: C.muted }}>
              parsed filter → <span style={{ color: C.ai }}>{JSON.stringify(filter)}</span>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
              {EXAMPLES.map((ex, i) => (
                <button key={i} className="chip" onClick={() => runQuery(ex)} style={{ fontSize: 11, color: C.muted, background: "transparent", border: `0.5px solid ${C.border}`, borderRadius: 16, padding: "4px 10px", cursor: "pointer" }}>{ex}</button>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 16, marginTop: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 600px", minWidth: 320 }}>
            {/* EF-CORE idle proposal */}
            <div style={{ background: C.aiBg, border: `0.5px solid ${C.ai}33`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Sparkles size={14} color={C.ai} />
                <span style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.8, color: C.ai }}>EF-CORE · PIPELINE</span>
                <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 9, color: C.ai }}>confidence 0.78</span>
              </div>
              <p style={{ margin: "10px 0 0", fontSize: 13.5, lineHeight: 1.5, color: "#2A2A2E" }}>
                <b>M&M analytics</b> has had no activity in <b>95 days</b> and is still in the lead stage — stalled after the
                sponsor reorg. Re-engage with a short value recap, or move to lost if there’s no response in two weeks.
              </p>
              <button onClick={() => setDrawer(DEALS[6])} style={{ marginTop: 12, background: C.ai, color: "#fff", border: "none", borderRadius: 8, padding: "7px 13px", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>Review idle deal</button>
            </div>

            {/* Kanban board */}
            {view === "loading" ? <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12 }}><Skeleton /></div>
             : view === "error" ? <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12 }}><ErrorState /></div>
             : view === "empty" ? <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12 }}><EmptyState /></div>
             : (
              <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 6 }}>
                {STAGES.map(stage => {
                  const cards = DEALS.filter(d => d.stage === stage);
                  return (
                    <div key={stage} style={{ flex: "1 0 160px", minWidth: 160 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 4px 8px" }}>
                        <span style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: STAGE_COLOR[stage] }}>{stage}</span>
                        <span style={{ fontFamily: mono, fontSize: 10, color: C.muted }}>{cards.length}</span>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {cards.map((d, i) => {
                          const dim = filter && !isHighlighted(d);
                          const hi = filter && isHighlighted(d);
                          return (
                            <div key={i} onClick={() => setDrawer(d)} style={{ background: C.surface, border: `0.5px solid ${hi ? C.ai : C.border}`, borderRadius: 10, padding: 11, cursor: "pointer", opacity: dim ? 0.35 : 1, boxShadow: hi ? `0 0 0 2px ${C.ai}22` : "none" }}>
                              <div style={{ fontSize: 12.5, fontWeight: 500, lineHeight: 1.3 }}>{d.name}</div>
                              <div style={{ fontFamily: mono, fontSize: 14, marginTop: 6 }}>{fmtINR(d.amount)}</div>
                              <div style={{ fontFamily: mono, fontSize: 10, color: C.muted, marginTop: 2 }}>weighted {fmtINR(d.weighted)} · {Math.round(d.p * 100)}%</div>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                                <span style={{ fontSize: 10.5, color: C.muted }}>{d.owner.split(" ")[0]}</span>
                                {d.idle >= 90 && <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontFamily: mono, fontSize: 9, color: C.warning }}><Clock size={10} />{d.idle}d</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Agreements rail */}
          <div style={{ flex: "0 0 240px", minWidth: 210 }}>
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
                <FileSignature size={13} color={C.muted} />
                <span style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Agreements</span>
              </div>
              {AGREEMENTS.map((a, i) => {
                const col = a.status === "active" ? C.success : a.status === "sent" ? C.primary : C.muted;
                return (
                  <div key={i} style={{ marginTop: 12, paddingTop: i ? 12 : 0, borderTop: i ? `0.5px solid ${C.border}` : "none" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 12.5, fontWeight: 500 }}>{a.title}</span>
                      <Badge color={col}>{a.status}</Badge>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
                      <span style={{ fontSize: 11, color: C.muted }}>{a.party.split(" ")[0]}</span>
                      <span style={{ fontFamily: mono, fontSize: 11 }}>{fmtINR(a.value)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: 16, marginTop: 16 }}>
              <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Controls</div>
              {[["Conversational queries", "NL → whitelisted filter"], ["RBAC / ABAC", "owner-scoped deals"], ["Append-only audit", "every stage change"]].map((r, i) => (
                <div key={i} style={{ display: "flex", gap: 9, alignItems: "flex-start", marginTop: 12 }}>
                  <ShieldCheck size={15} color={C.success} style={{ marginTop: 1, flexShrink: 0 }} />
                  <div><div style={{ fontSize: 12.5, fontWeight: 500 }}>{r[0]}</div><div style={{ fontSize: 11, color: C.muted }}>{r[1]}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {drawer && <DealDrawer deal={drawer} onClose={() => setDrawer(null)} />}
    </div>
  );
}

function Skeleton() { return <div style={{ padding: 14 }}>{[...Array(5)].map((_, i) => <div key={i} style={{ height: 18, background: C.border, borderRadius: 6, marginBottom: 14, opacity: 0.6, animation: "pulse 1.4s ease-in-out infinite" }} />)}<style>{`@keyframes pulse{0%,100%{opacity:.35}50%{opacity:.7}}`}</style></div>; }
function ErrorState() { return <div style={{ padding: 40, textAlign: "center" }}><AlertTriangle size={26} color={C.danger} /><div style={{ marginTop: 10, fontWeight: 500 }}>Couldn’t load pipeline</div><button style={{ marginTop: 14, display: "inline-flex", alignItems: "center", gap: 6, background: C.primary, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, cursor: "pointer" }}><RefreshCw size={13} /> Retry</button></div>; }
function EmptyState() { return <div style={{ padding: 44, textAlign: "center" }}><Inbox size={26} color={C.muted} /><div style={{ marginTop: 10, fontWeight: 500 }}>No deals in pipeline</div><div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Add a deal or import from your CRM to begin.</div></div>; }

function DealDrawer({ deal, onClose }) {
  const [done, setDone] = useState(null);
  const acts = ACTIVITIES[deal.name] || [];
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "#08090C55", display: "flex", justifyContent: "flex-end", zIndex: 50 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 400, background: C.surface, height: "100%", padding: 22, overflow: "auto", boxShadow: "-12px 0 40px #0003" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Badge color={STAGE_COLOR[deal.stage]}>{deal.stage}</Badge>
          <X size={18} color={C.muted} style={{ cursor: "pointer" }} onClick={onClose} />
        </div>
        <h3 style={{ margin: "10px 0 2px", fontSize: 17 }}>{deal.name}</h3>
        <div style={{ fontSize: 13, color: C.muted }}>{deal.cust}</div>

        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          {[["Amount", fmtINR(deal.amount)], ["Weighted", fmtINR(deal.weighted)], ["Win %", `${Math.round(deal.p * 100)}%`]].map((x, i) => (
            <div key={i} style={{ flex: 1, background: C.bg, borderRadius: 10, padding: "10px 8px", textAlign: "center" }}>
              <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", color: C.muted }}>{x[0]}</div>
              <div style={{ fontFamily: mono, fontSize: 13, marginTop: 4 }}>{x[1]}</div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 14, fontSize: 12.5 }}><span style={{ color: C.muted }}>Owner:</span> {deal.owner}</div>
        <div style={{ marginTop: 4, fontSize: 12.5 }}><span style={{ color: C.muted }}>Next step:</span> {deal.next}</div>

        {deal.idle >= 90 && (
          <div style={{ marginTop: 14, background: "#FBF2E0", border: `0.5px solid ${C.warning}33`, borderRadius: 8, padding: 11 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, fontWeight: 500, color: C.warning }}>
              <Clock size={14} /> Idle {deal.idle} days
            </div>
            <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4 }}>No activity beyond the 90-day threshold — flagged by EF-CORE for re-engagement.</div>
          </div>
        )}

        <div style={{ marginTop: 16, fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Activity timeline</div>
        {acts.length ? acts.map((a, i) => {
          const Ic = ACT_ICON[a.kind] || StickyNote;
          return (
            <div key={i} style={{ display: "flex", gap: 10, marginTop: 12 }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: C.bg, display: "grid", placeItems: "center", flexShrink: 0 }}><Ic size={14} color={C.muted} /></div>
              <div>
                <div style={{ fontSize: 12.5 }}>{a.text}</div>
                <div style={{ fontFamily: mono, fontSize: 10, color: C.muted, marginTop: 2 }}>{a.who} · {a.when}</div>
              </div>
            </div>
          );
        }) : <div style={{ fontSize: 12, color: C.muted, marginTop: 10 }}>No activity recorded yet.</div>}

        {done ? (
          <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 8, background: "#E9F7F1", border: `0.5px solid ${C.success}33`, borderRadius: 9, padding: 12, fontSize: 13, color: C.success }}>
            <CheckCircle2 size={15} /> {done}. Logged to the audit trail.
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
            <button onClick={() => setDone("Re-engagement task created")} style={{ flex: 1, background: C.primary, color: "#fff", border: "none", borderRadius: 9, padding: "11px 0", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>Create task</button>
            <button onClick={() => setDone("Deal advanced a stage")} style={{ flex: 1, background: "transparent", color: C.muted, border: `0.5px solid ${C.border}`, borderRadius: 9, padding: "11px 0", fontSize: 13, cursor: "pointer" }}>Advance stage</button>
          </div>
        )}
      </div>
    </div>
  );
}
